import React, { useCallback, useState } from 'react';
import {
	Candidate,
	PreworkCandidateSummary,
} from '../../shared/PreWorkServiceTypes';
import { useTranslation } from 'react-i18next';
import { CandidateTableColumnsPreWork } from './ColumnsPrework';
import { Button, Table } from 'antd';
import {
	PREWORK_ADD_MORE_CANDIDATES,
	PREWORK_ASSIGN_MENTOR,
	PREWORKGROUPS,
} from '../../shared/Routes';
import {
	PreworkGroupTabs,
	MAX_CANDIDATES_FOR_GROUP,
} from '../../shared/Constants';
import { usePreworkGroupId } from '../../hooks/usePreworkGroupId';
import { CandidatesTableColumnsMentorPreWork } from './AssignMentorPage/ColumnsMentor';
import { useEditGroupPreWork } from './EditGroupPage/useEditGroup';
import useUserSession from '../../hooks/useUserSession';
import { hasPrivilege } from '../../shared/Utils';
import { PrivilegeCodes } from '../../shared/RoleConstants';

export const GroupCandidatesTablePrework = ({
	groupCandidates,
	isEditGroupTable = false,
	mentor,
}: {
	groupCandidates: Array<PreworkCandidateSummary> | undefined;
	isEditGroupTable?: boolean;
	mentor?: Candidate;
}) => {
	const { t } = useTranslation();
	const {
		navigateWithPreworkGroupId,
		preworkGroupId,
		navigateWithPreworkBatchIdAndGroupTab,
	} = usePreworkGroupId();
	const { updateGroup } = useEditGroupPreWork();
	const groupCandidateIds =
		groupCandidates &&
		groupCandidates.map((candidate) => candidate.applicationId);
	const [selectedGroupCandidates, setSelectedGroupCandidates] = useState<
		React.Key[]
	>([]);
	const { loggedInUserPrivileges } = useUserSession();

	const onSelectChange = (newSelectedCandidateKeys: React.Key[]) => {
		setSelectedGroupCandidates(newSelectedCandidateKeys);
	};

	const groupCandidateSelection = {
		selectedGroupCandidates,
		onChange: onSelectChange,
	};

	const showRemove = hasPrivilege(
		PrivilegeCodes.UPDATE_PREWORK_GROUP,
		loggedInUserPrivileges
	);

	const showAssignMentor = hasPrivilege(
		PrivilegeCodes.ASSIGN_PREWORK_MENTOR,
		loggedInUserPrivileges
	);

	const hasSelected = selectedGroupCandidates.length > 0;
	const disableAddMore =
		groupCandidateIds && groupCandidateIds.length === MAX_CANDIDATES_FOR_GROUP;

	const handleRemoveClick = useCallback(() => {
		const candidateIdsToBeUpdated =
			groupCandidateIds &&
			groupCandidateIds.filter((id) => !selectedGroupCandidates.includes(id));
		if (candidateIdsToBeUpdated) {
			updateGroup(
				candidateIdsToBeUpdated,
				'areYouSureToRemoveGroupPrework',
				false
			);
		}
	}, [groupCandidateIds, selectedGroupCandidates, updateGroup]);

	const handleAddMoreClick = useCallback(() => {
		navigateWithPreworkGroupId(PREWORK_ADD_MORE_CANDIDATES, preworkGroupId);
	}, [navigateWithPreworkGroupId, preworkGroupId]);

	const handleAssignMentorClick = useCallback(() => {
		navigateWithPreworkGroupId(PREWORK_ASSIGN_MENTOR, preworkGroupId);
	}, [navigateWithPreworkGroupId, preworkGroupId]);

	const handleBackClick = useCallback(() => {
		navigateWithPreworkBatchIdAndGroupTab(
			PREWORKGROUPS,
			PreworkGroupTabs.GROUPS_LIST_VIEW
		);
	}, [navigateWithPreworkBatchIdAndGroupTab]);

	const columns = CandidateTableColumnsPreWork(true);
	const columnsMentor = CandidatesTableColumnsMentorPreWork(true);

	if (
		groupCandidates &&
		groupCandidates.length &&
		groupCandidates.length === 0
	) {
		return <div>{t('noDataFound')}</div>;
	}

	return (
		<>
			{groupCandidates && (
				<>
					<h4>{t('groupMembers')}</h4>
					<Table
						rowSelection={
							isEditGroupTable ? groupCandidateSelection : undefined
						}
						scroll={{ x: true }}
						dataSource={groupCandidates}
						pagination={false}
						columns={columns}
					/>
					{!isEditGroupTable && mentor && (
						<div className="mt-5">
							<h4>{t('mentor')}</h4>
							<Table
								scroll={{ x: true }}
								dataSource={[mentor]}
								pagination={false}
								columns={columnsMentor}
								rowKey={(record) => {
									return `mentor-row-${record.id}`;
								}}
							/>
						</div>
					)}
					<div className="flex flex-row-reverse items-center gap-4 my-8">
						{isEditGroupTable && (
							<>
								<Button
									type="primary"
									onClick={handleAddMoreClick}
									disabled={disableAddMore}
								>
									{t('addMore')}
								</Button>
								{showRemove && (
									<Button
										type="primary"
										onClick={handleRemoveClick}
										disabled={!hasSelected}
									>
										{t('remove')}
									</Button>
								)}
							</>
						)}
						<>
							{!isEditGroupTable && showAssignMentor && (
								<Button type="primary" onClick={handleAssignMentorClick}>
									{t('assignMentor')}
								</Button>
							)}
							<Button type="default" onClick={handleBackClick}>
								{t('back')}
							</Button>
						</>
					</div>
				</>
			)}
		</>
	);
};
